import { Component, Input, OnChanges, Pipe, PipeTransform, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-sql';
import { CodeFile, CodingAnswer, CodingLanguage, LANGUAGE_LABELS, parseCodingAnswer } from '../../services/coding-answer.model';
import { DiffService } from '../../services/diff.service';
import { DiffHunk, DiffRow } from '../../services/coding-diff';

export type FileStatus = 'new' | 'deleted' | 'renamed' | 'modified' | 'unchanged';

interface ViewPart {
  html: SafeHtml;
  changed: boolean;
}

interface ViewRow {
  /** `typing` is the line still being written while a change streams in. */
  kind: 'context' | 'remove' | 'add' | 'typing';
  oldLine?: number;
  newLine?: number;
  parts: ViewPart[];
}

interface ViewHunk {
  kind: 'context' | 'change';
  rows: ViewRow[];
  head: ViewRow[];
  hidden: ViewRow[];
  tail: ViewRow[];
  expanded: boolean;
  steps: string[];
  notes: string[];
}

export interface FileView {
  id: string;
  path: string;
  language: CodingLanguage;
  before?: CodeFile;
  after?: CodeFile;
  status: FileStatus;
  edits: number;
  hunks: ViewHunk[];
  /** Notes that belong to the whole file (or to a fresh solution's sections, in order). */
  notes: string[];
  /** The complete resulting file, syntax highlighted. */
  fullHtml: SafeHtml;
  showFull: boolean;
}

/** Splits a step such as "change `PRINT` to `print`" into text and code parts for rendering. */
@Pipe({ name: 'codeSpans', standalone: true })
export class CodeSpansPipe implements PipeTransform {
  transform(text: string): Array<{ text: string; code: boolean }> {
    return (text || '').split('`').map((part, index) => ({ text: part, code: index % 2 === 1 })).filter(part => part.text);
  }
}

const escapeHtml = (text: string): string => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Renders one coding-mode answer (a tagged JSON envelope in the chat history).
 *
 * - A fresh solution (no previous code) looks exactly like an ordinary code answer: syntax-highlighted
 *   code block(s) plus the explanation.
 * - A change to existing code is shown as the app's dark diff view: red rows to remove, green rows to
 *   add, the changed words highlighted, and a plain-English step under each change group.
 * Read-only; the same component serves the helper chat and the learner/interview page.
 */
@Component({
  selector: 'app-coding-answer',
  standalone: true,
  imports: [CommonModule, CodeSpansPipe],
  templateUrl: './coding-answer.component.html',
  styleUrls: ['./coding-answer.component.scss']
})
export class CodingAnswerComponent implements OnChanges {
  @Input() text = '';
  @Input() theme: 'light' | 'dark' = 'light';

  answer: CodingAnswer | null = null;
  files: FileView[] = [];
  selectedFileId = '';
  /** Fresh solutions show code only, like any other code answer; the explanation is opened on demand. */
  showNotes = false;
  labels = LANGUAGE_LABELS;
  /** While the answer streams in: the code written so far (new files, or code being read from a screenshot). */
  streamFiles: Array<{ path: string; language: CodingLanguage; html: SafeHtml }> = [];
  /** True while the code being shown is the learner's code read from a screenshot, before the fix arrives. */
  streamReading = false;
  /** A finished answer that changes nothing (e.g. the code is already correct): its code stays on screen. */
  noChanges = false;
  private htmlCache = new Map<string, SafeHtml>();

  constructor(private diff: DiffService, private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['text']) this.build();
  }

  get selected(): FileView | undefined {
    return this.files.find(file => file.id === this.selectedFileId);
  }

  /** A first answer with no previous code: shown as a normal code answer, not as a diff. */
  get fresh(): boolean {
    return !!this.answer && this.answer.baseline.length === 0;
  }

  /** The language shown in the header: the file on screen decides (older answers may carry a stale top-level one). */
  get language(): CodingLanguage {
    const answer = this.answer!;
    return this.selected?.language || answer.files[0]?.language || answer.baseline[0]?.language || answer.language;
  }

  /** Lines the learner has to touch across all files. */
  get edits(): number {
    return this.files.reduce((sum, file) => sum + file.edits, 0);
  }

  get hasNotes(): boolean {
    return this.files.some(file => file.notes.length > 0);
  }

  select(id: string): void {
    this.selectedFileId = id;
  }

  private build(): void {
    const previous = this.answer;
    this.answer = parseCodingAnswer(this.text);
    this.files = [];
    this.streamFiles = [];
    this.streamReading = false;
    this.noChanges = false;
    // Keep "Explain the code" open while the same answer finishes streaming.
    if (!previous?.streaming) this.showNotes = false;
    if (!this.answer) return;
    const keepSelection = previous?.streaming ? this.selectedFileId : '';
    if (this.answer.streaming) {
      this.buildStreaming(keepSelection);
      return;
    }
    const before = this.answer.baseline;
    const after = this.answer.files;
    const ids = [...new Set([...before, ...after].map(file => file.id))];
    this.files = ids.map(id => {
      const old = before.find(file => file.id === id);
      const next = after.find(file => file.id === id);
      const language = (next || old)!.language;
      const notes = this.answer!.notes.filter(note => note.fileId === id);
      const diff = this.diff.generateFileDiff(old?.content, next?.content, notes);
      const status: FileStatus = !old ? 'new' : !next ? 'deleted'
        : old.path !== next.path ? 'renamed' : diff.changes ? 'modified' : 'unchanged';
      const fresh = before.length === 0;
      return {
        id,
        path: (next || old)!.path,
        language,
        before: old,
        after: next,
        status,
        edits: diff.edits,
        hunks: fresh ? [] : diff.hunks.map(hunk => this.toViewHunk(hunk, language)),
        notes: fresh ? notes.map(note => note.text) : diff.fileNotes,
        fullHtml: this.highlight(next?.content ?? '', language),
        showFull: false
      };
    });
    // Nothing to change: keep the code that was just shown, fully expanded in the same block, instead of hiding it
    // behind "No changes in this file."
    this.noChanges = !this.fresh && this.files.length > 0 && this.files.every(file => file.status === 'unchanged');
    if (this.noChanges) this.files.forEach(file => file.hunks.forEach(hunk => hunk.expanded = true));
    const first = this.files.find(file => file.status !== 'unchanged') || this.files[0];
    this.selectedFileId = this.files.some(file => file.id === keepSelection) ? keepSelection : first?.id || '';
  }

  /**
   * While the answer streams in, it is drawn in the same place and style as the finished answer, so nothing is
   * swapped at the end: a new question shows code being typed, a change shows red / green lines growing (the
   * notes are added when the answer is complete), and code read from a screenshot appears in the same block.
   */
  private buildStreaming(keepSelection: string): void {
    const answer = this.answer!;
    if (this.fresh) {
      this.streamFiles = answer.files.map(file => ({ path: file.path, language: file.language, html: this.highlight(file.content, file.language) }));
      return;
    }
    this.streamReading = !answer.files.length && answer.transcribed;
    const view = (id: string, path: string, language: CodingLanguage, hunks: ViewHunk[], edits: number,
        status: FileStatus, before?: CodeFile, after?: CodeFile): FileView =>
      ({ id, path, language, before, after, status, edits, hunks, notes: [], fullHtml: '', showFull: false });
    if (this.streamReading) {
      // The learner's code being read from the screenshot: plain lines, in the diff block it will turn into.
      this.files = answer.baseline.map(file => {
        const rows: ViewRow[] = file.content.split('\n').map((line, index) => ({
          kind: 'context', oldLine: index + 1, newLine: index + 1, parts: [{ html: this.highlight(line, file.language), changed: false }]
        }));
        const hunk: ViewHunk = { kind: 'context', rows, head: rows, hidden: [], tail: [], expanded: true, steps: [], notes: [] };
        return view(file.id, file.path, file.language, [hunk], 0, 'unchanged', file, undefined);
      });
    } else {
      this.files = answer.files.map(after => {
        const before = answer.baseline.find(file => file.id === after.id);
        const diff = this.diff.generateStreamingFileDiff(before?.content, after.content);
        const status: FileStatus = !before ? 'new' : before.path !== after.path ? 'renamed' : 'modified';
        const hunks = diff.hunks.map(hunk => this.toViewHunk(hunk, after.language));
        // The line still being written is typed out at the end; it takes its red / green place once complete.
        const lines = after.content.split('\n');
        const typing = lines[lines.length - 1];
        if (typing) {
          const row: ViewRow = { kind: 'typing', newLine: lines.length, parts: [{ html: this.highlight(typing, after.language), changed: false }] };
          hunks.push({ kind: 'context', rows: [row], head: [row], hidden: [], tail: [], expanded: true, steps: [], notes: [] });
        }
        return view(after.id, after.path, after.language, hunks, diff.edits, status, before, after);
      });
    }
    this.selectedFileId = this.files.some(file => file.id === keepSelection) ? keepSelection : this.files[0]?.id || '';
  }

  private toViewHunk(hunk: DiffHunk, language: CodingLanguage): ViewHunk {
    const convert = (rows: DiffRow[]) => rows.map(row => this.toViewRow(row, language));
    return {
      kind: hunk.kind,
      rows: convert(hunk.rows),
      head: convert(hunk.head),
      hidden: convert(hunk.hidden),
      tail: convert(hunk.tail),
      expanded: hunk.expanded,
      steps: hunk.steps,
      notes: hunk.notes
    };
  }

  private toViewRow(row: DiffRow, language: CodingLanguage): ViewRow {
    const text = row.segments.map(segment => segment.text).join('');
    // Paired lines: box only the changed parts. Indentation at the start of a changed part is shown as dots
    // ("····print"), so an added or removed indent is visible; spaces inside the code stay as written.
    if (row.paired) {
      const parts: ViewPart[] = row.segments.map(segment => ({
        html: segment.changed ? this.highlightChanged(segment.text, language) : this.highlight(segment.text, language),
        changed: segment.changed
      }));
      return { kind: row.kind, oldLine: row.oldLine, newLine: row.newLine, parts };
    }
    // A whole line added or removed: every character after the indentation is a change, so box it.
    if (row.kind !== 'context') {
      const indent = (text.match(/^\s*/) || [''])[0];
      const body = text.slice(indent.length);
      const parts: ViewPart[] = [{ html: this.highlight(indent, language), changed: false }];
      if (body) parts.push({ html: this.highlight(body, language), changed: true });
      return { kind: row.kind, oldLine: row.oldLine, newLine: row.newLine, parts };
    }
    return { kind: row.kind, oldLine: row.oldLine, newLine: row.newLine, parts: [{ html: this.highlight(text, language), changed: false }] };
  }

  /** Prism-highlighted HTML (Prism escapes the source, so the result is safe to bind). */
  /**
   * Highlighted HTML, cached: while an answer streams in, the same lines are drawn many times a second, and
   * handing Angular the same object for an unchanged line means that line's DOM is left alone (no flicker).
   */
  private highlight(code: string, language: CodingLanguage): SafeHtml {
    return this.cached('p:' + language + ':' + code, () => this.highlightHtml(code, language));
  }

  /** A changed part: its leading whitespace becomes visible dots / arrows, the rest is highlighted code. */
  private highlightChanged(code: string, language: CodingLanguage): SafeHtml {
    return this.cached('c:' + language + ':' + code, () => {
      const lead = (code.match(/^\s*/) || [''])[0];
      const dots = escapeHtml(lead.replace(/ /g, '·').replace(/\t/g, '→'));
      return dots + this.highlightHtml(code.slice(lead.length), language);
    });
  }

  private cached(key: string, html: () => string): SafeHtml {
    let value = this.htmlCache.get(key);
    if (!value) {
      if (this.htmlCache.size > 3000) this.htmlCache.clear();
      value = this.sanitizer.bypassSecurityTrustHtml(html());
      this.htmlCache.set(key, value);
    }
    return value;
  }

  /** Stable identities for *ngFor, so rows that did not change keep their DOM while the answer streams in. */
  trackFile = (_: number, file: FileView) => file.id;
  trackHunk = (index: number, hunk: ViewHunk) => index + ':' + hunk.kind;
  trackRow = (_: number, row: ViewRow) => row.kind + ':' + (row.oldLine ?? '') + ':' + (row.newLine ?? '');
  trackIndex = (index: number) => index;

  private highlightHtml(code: string, language: CodingLanguage): string {
    if (!code) return '';
    try {
      const grammar = Prism.languages[language];
      return grammar ? Prism.highlight(code, grammar, language) : escapeHtml(code);
    } catch {
      return escapeHtml(code);
    }
  }
}
